/**
 * WebView 通信ユーティリティ
 *
 * Hokage 等のネイティブアプリの WKWebView 内で動作している場合に、
 * Native 側にメッセージを送信したり、レスポンスを受け取るための薄いラッパー。
 *
 * - Hokage: `window.ReactNativeWebView.postMessage` を `webkit.messageHandlers.nativeApp` に
 *   ブリッジする UserScript を注入している（参考: `Hokage/ios/Hokage/Views/WebViewScreen.swift`）
 * - Native からのレスポンスは `window.handleNativeEvent(data)` 呼び出しで返ってくる
 */

const DEFAULT_TIMEOUT_MS = 3000;

/**
 * WebView 環境かどうかを判定する。
 *
 * - `window.ReactNativeWebView`（Hokage が注入する bridge）
 * - `window.webkit.messageHandlers.nativeApp`（Hokage の messageHandler）
 * - `window.webkit.messageHandlers.nativeHandler`（Utage-Web 互換のフォールバック）
 *
 * のいずれかが存在すれば WebView 環境とみなす。
 */
export const isWebView = (): boolean => {
  return (
    !!window.ReactNativeWebView ||
    !!window.webkit?.messageHandlers?.nativeApp ||
    !!window.webkit?.messageHandlers?.nativeHandler
  );
};

/**
 * Native にメッセージを送信する（fire-and-forget、レスポンス不要）。
 * `window.handleNativeEvent` を触らないため、他のリクエスト/レスポンス通信と競合しない。
 *
 * 優先順位:
 * 1. `window.ReactNativeWebView.postMessage`（Hokage が注入する bridge）
 * 2. `window.webkit.messageHandlers.nativeApp`（Hokage）
 * 3. `window.webkit.messageHandlers.nativeHandler`（Utage-Web 互換）
 */
export const postMessageToNative = (message: unknown): void => {
  try {
    if (window.ReactNativeWebView?.postMessage) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
      return;
    }
    if (window.webkit?.messageHandlers?.nativeApp) {
      window.webkit.messageHandlers.nativeApp.postMessage(message);
      return;
    }
    if (window.webkit?.messageHandlers?.nativeHandler) {
      window.webkit.messageHandlers.nativeHandler.postMessage(message);
      return;
    }
  } catch {
    // fire-and-forget のためエラーは無視
  }
};

/**
 * Native にメッセージを送信し、レスポンスがあれば取得する。
 *
 * Native 側が `window.handleNativeEvent(data)` を呼び出すことで Promise が解決される。
 * タイムアウト時間内にレスポンスがない場合は `null` で解決する（reject しない）。
 *
 * 注意: 一度に処理できるリクエストは 1 件のみ。並行呼び出しは未サポート
 * （Utage-Web の `sendMessageToWebView` と同じ制約）。
 */
export function sendMessageToWebView<T = unknown>(
  message: unknown,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T | null> {
  return new Promise((resolve) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    const settle = (value: T | null) => {
      if (settled) return;
      settled = true;
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      if (window.handleNativeEvent) delete window.handleNativeEvent;
      resolve(value);
    };

    try {
      window.handleNativeEvent = (data: unknown) => {
        settle(data as T);
      };

      timeoutId = setTimeout(() => settle(null), timeoutMs);

      if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
      } else if (window.webkit?.messageHandlers?.nativeApp) {
        window.webkit.messageHandlers.nativeApp.postMessage(message);
      } else if (window.webkit?.messageHandlers?.nativeHandler) {
        window.webkit.messageHandlers.nativeHandler.postMessage(message);
      } else {
        // WebView ブリッジが存在しない（Web ブラウザ等）→ レスポンスは来ないので即時 null
        settle(null);
      }
    } catch {
      settle(null);
    }
  });
}
